<template>
    <div class="hero-card">
        <div class="hero-copy">
            <div>
                <span class="eyebrow">Старт подключения</span>
                <h2>Создаём проект и готовим интеграцию</h2>
                <p>
                    Сначала задаём название проекта и контактный номер. Затем выбираем часовую зону,
                    CRM-систему и способ подключения.
                </p>
            </div>
            <div class="hero-points">
                <div>
                    <b>01</b>
                    <span>Проект и контакт для связи.</span>
                </div>
                <div>
                    <b>02</b>
                    <span>Часовой пояс для расписаний и отчётов.</span>
                </div>
                <div>
                    <b>03</b>
                    <span>CRM и данные для интеграции.</span>
                </div>
            </div>
        </div>

        <div class="mode-card">
            <div class="mode-head">
                <div class="mode-icon">DH</div>
                <div>
                    <strong>Данные проекта</strong>
                    <span>Будут использоваться в системе Data Heroes</span>
                </div>
            </div>

            <div class="form-line">
                <div class="field-label">Название проекта</div>
                <input
                    v-model="projectName"
                    class="input"
                    type="text"
                    maxlength="40"
                    placeholder="Введите название проекта"
                    :disabled="loading"
                    @keydown.enter.prevent="submit"
                />
                <div v-if="nameError" class="field-error">{{ nameError }}</div>
            </div>

            <div v-if="showPhoneField" class="form-line">
                <div class="field-label">Номер телефона для связи в WhatsApp</div>
                <ValidationInput
                    v-model="phone"
                    :rule="phoneValidator"
                    label="+7 (999) 123-45-67"
                    class="phone-input"
                />
                <div class="helper">
                    Обычно это номер руководителя салона. Он нужен для уведомлений по оплате и связи с
                    менеджером.
                </div>
            </div>

            <button
                v-if="!hideSubmitButton"
                class="ob-btn"
                type="button"
                :disabled="!canSubmit || loading"
                @click="submit"
            >
                <v-progress-circular
                    v-if="loading"
                    indeterminate
                    size="18"
                    width="2"
                    color="white"
                    class="mr-2"
                />
                Продолжить
            </button>
        </div>
    </div>
</template>

<script>
import { computed, getCurrentInstance, onMounted, onBeforeUnmount, ref, watch, inject } from 'vue'
import api from '@/api/api'
import { clientFormHelper } from '@/components/clientForm/helpers'
import phoneValidator, { isValidPhone } from '@/helpers/validators/phoneValidator'
import ValidationInput from '@/components/inputs/ValidationInput.vue'
import useRouter from '@/hooks/useRouter'
import {
    setRedirectAfterOnboardingPath,
    toTemplatedRedirectPath,
} from '@/helpers/redirectAfterOnboarding'

const PROJECT_NAME_MAX = 40

export default {
    name: 'CreateProjectStep',
    components: { ValidationInput },
    props: {
        redirectAfterOnboardingPath: {
            type: String,
            default: null,
        },
        hideSubmitButton: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        const root = getCurrentInstance().proxy
        const { router } = useRouter()
        const wizardNav = inject('onboardingWizardNav', null)
        const projectName = ref('')
        const phone = ref('')
        const loading = ref(false)
        const clientFormExists = ref(true)

        const showPhoneField = computed(() => clientFormExists.value === false)

        const nameError = computed(() => {
            const name = (projectName.value || '').trim()
            if (!name) return ''
            if (name.length > PROJECT_NAME_MAX) {
                return `Максимальная длина: ${PROJECT_NAME_MAX} символов`
            }
            return ''
        })

        const canSubmit = computed(() => {
            const name = (projectName.value || '').trim()
            if (!name || name.length > PROJECT_NAME_MAX) return false
            if (showPhoneField.value && !isValidPhone(phone.value)) return false
            return true
        })

        onMounted(async () => {
            try {
                const res = await api.clientForm.getClientFormExists()
                clientFormExists.value = Boolean(res?.data?.value)
            } catch (e) {
                // Если не удалось проверить — показываем поле телефона, чтобы не потерять сбор контакта
                clientFormExists.value = false
            }
        })

        const saveClientForm = async (projectId) => {
            const answers = {
                phone: phone.value,
                functions: [],
                businessType: null,
                customBusinessType: '',
            }
            const mappedAnswers = clientFormHelper.mapAnswers('projectCreate', answers, {
                q1Answers: [],
                q2Answers: [],
            })
            await api.clientForm.saveAnswers({
                answers,
                reason: 'projectCreate',
                mappedAnswers,
                projectId,
            })
        }

        const submit = async () => {
            if (!canSubmit.value || loading.value) return
            loading.value = true
            wizardNav?.set?.({ nextLoading: true })
            try {
                const project = await root.$store.dispatch('project/createProject', {
                    projectName: projectName.value.trim(),
                    isOnBoardingActive: true,
                    billingEnabledStatus: true,
                })

                if (showPhoneField.value) {
                    await saveClientForm(project.id)
                }

                if (props.redirectAfterOnboardingPath) {
                    setRedirectAfterOnboardingPath(
                        toTemplatedRedirectPath(props.redirectAfterOnboardingPath),
                    )
                }

                await router.push(`/project/${project.id}/onBoarding`)
            } catch (e) {
                root.$store.dispatch(
                    'callNotify',
                    e?.message || 'Не удалось создать проект. Попробуйте ещё раз.',
                )
            } finally {
                loading.value = false
                wizardNav?.set?.({ nextLoading: false })
            }
        }

        const syncWizardNav = () => {
            if (!wizardNav || !props.hideSubmitButton) return
            wizardNav.set({
                canBack: false,
                canNext: canSubmit.value && !loading.value,
                nextLoading: loading.value,
                nextLabel: 'Далее',
                onNext: submit,
                onBack: null,
            })
        }

        watch([canSubmit, loading, () => props.hideSubmitButton], syncWizardNav, { immediate: true })

        onBeforeUnmount(() => {
            if (props.hideSubmitButton) {
                wizardNav?.reset?.()
            }
        })

        return {
            projectName,
            phone,
            loading,
            showPhoneField,
            nameError,
            canSubmit,
            phoneValidator,
            submit,
            hideSubmitButton: computed(() => props.hideSubmitButton),
        }
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

$ob-primary-2: #3F8EFC

.hero-card
    width: 100%
    max-width: 100%
    min-width: 0
    display: grid
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr)
    gap: 24px
    padding: 28px
    border: 1px solid rgba(229, 232, 242, 0.94)
    border-radius: 24px
    background: linear-gradient(135deg, #FFFFFF 0%, #F8FAFF 100%)
    box-shadow: 0 16px 42px rgba(26, 26, 46, 0.07)
    box-sizing: border-box

.hero-copy
    position: relative
    overflow: hidden
    border-radius: 20px
    background: linear-gradient(135deg, #101629 0%, #29336D 100%)
    color: #fff
    padding: 28px
    display: flex
    flex-direction: column
    justify-content: space-between
    min-height: 320px
    min-width: 0
    max-width: 100%
    box-sizing: border-box
    &:after
        content: ""
        position: absolute
        right: -80px
        bottom: -96px
        width: 220px
        height: 220px
        border-radius: 50%
        background: rgba(212, 236, 56, 0.22)

.eyebrow
    display: inline-flex
    align-items: center
    gap: 8px
    color: $pop-new !important
    font-size: 11px
    font-weight: 900
    text-transform: uppercase
    letter-spacing: 0.12em
    margin-bottom: 10px
    position: relative
    z-index: 1

.hero-copy
    h2
        font-size: 30px
        line-height: 1.05
        letter-spacing: -0.055em
        margin: 0 0 12px
        max-width: 420px
        position: relative
        z-index: 1
        font-weight: 800
        color: #fff !important
    p
        margin: 0
        color: rgba(255, 255, 255, 0.72) !important
        line-height: 1.58
        max-width: 420px
        position: relative
        z-index: 1

.hero-points
    display: grid
    gap: 10px
    margin-top: 28px
    position: relative
    z-index: 1
    div
        display: grid
        grid-template-columns: 36px 1fr
        gap: 10px
        align-items: center
        padding: 10px 12px
        border: 1px solid rgba(255, 255, 255, 0.12)
        border-radius: 14px
        background: rgba(255, 255, 255, 0.07)
        color: inherit
    b
        color: $pop-new
        font-size: 12px
    span
        color: rgba(255, 255, 255, 0.78) !important
        font-size: 12px
        line-height: 1.35

.mode-card
    display: flex
    flex-direction: column
    gap: 16px
    justify-content: center
    padding: 8px 0
    min-width: 0
    max-width: 100%

.mode-head
    display: flex
    align-items: center
    gap: 12px
    strong
        display: block
        font-size: 18px
        letter-spacing: -0.035em
        color: $dark-new
    span
        display: block
        color: $muted-light-new
        font-size: 12px
        margin-top: 2px

.mode-icon
    width: 44px
    height: 44px
    border-radius: 14px
    background: $main-new
    color: #fff
    display: grid
    place-items: center
    font-weight: 900
    font-size: 12px
    box-shadow: 0 10px 24px rgba(82, 90, 213, 0.25)
    flex: 0 0 auto

.form-line
    display: grid
    gap: 0

.field-label
    font-size: 12px
    font-weight: 800
    color: #394155
    margin-bottom: 7px

.input
    width: 100%
    height: 44px
    border: 1px solid #D9DEEA
    border-radius: 11px
    background: #fff
    padding: 0 13px
    color: $dark-new
    outline: none
    font-size: 14px
    &:focus
        border-color: $main-new
        box-shadow: 0 0 0 4px rgba(82, 90, 213, 0.10)
    &:disabled
        opacity: 0.7

.field-error
    margin-top: 6px
    font-size: 12px
    color: $alert-new

.helper
    font-size: 12px
    color: $muted-light-new
    line-height: 1.45
    margin-top: 7px

.phone-input
    ::v-deep .v-input
        margin-top: 0
        padding-top: 0
    ::v-deep .v-text-field__details
        margin-bottom: 0

.ob-btn
    width: 100%
    height: 42px
    border: 0
    border-radius: 12px
    padding: 0 18px
    font-size: 13px
    font-weight: 900
    display: inline-flex
    align-items: center
    justify-content: center
    gap: 8px
    white-space: nowrap
    cursor: pointer
    background: $main-new
    color: #fff
    box-shadow: 0 10px 20px rgba(82, 90, 213, 0.24)
    &:disabled
        opacity: 0.55
        cursor: not-allowed
        box-shadow: none

@media (max-width: 900px)
    .hero-card
        grid-template-columns: 1fr
    .hero-copy
        min-height: auto

@media (max-width: 640px)
    .hero-card
        padding: 16px
        border-radius: 20px
        gap: 16px
    .hero-copy
        padding: 20px
        h2
            font-size: 22px
            max-width: 100%
        p
            max-width: 100%
    .hero-points div
        grid-template-columns: 32px minmax(0, 1fr)
</style>
